import { apiClient, buildStoreParams } from "@/services/api";
import type { Order } from "../types";

export interface OrderApiResponse {
	id: string;
	orderNumber?: string;
	status?: string;
	paymentStatus?: string;
	fulfilmentStatus?: string;
	customer?:
		| string
		| { id?: string; name: string; email?: string; phone?: string };
	shippingAddress?: {
		street?: string;
		city?: string;
		state?: string;
		zipCode?: string;
		country?: string;
		fullAddress?: string;
	};
	items?: Array<{
		id: string;
		productId: string;
		productTitle: string;
		quantity: number;
		unitPrice: number;
		totalPrice: number;
		thumbnail?: string;
		sku?: string;
		variantTitle?: string;
	}>;
	itemsCount?: number;
	subtotal?: number;
	shippingCost?: number;
	discount?: number;
	total?: number;
	currency?: string;
	notes?: string;
	thumbnail?: string;
	createdAt?: string;
	date?: string;
	updatedAt?: string;
	location?: string;
	storeId?: string;
	// Field names the backend really uses (amounts come as numeric strings).
	userId?: string | null;
	shippingAddressId?: string | null;
	paymentMethod?: string | null;
	subtotalBrl?: string | number;
	totalAmountBrl?: string | number;
	shippingAmountBrl?: string | number;
	discountAmountBrl?: string | number;
	metadata?: Record<string, unknown> | null;
	paidAt?: string | null;
	shippedAt?: string | null;
	deliveredAt?: string | null;
	canceledAt?: string | null;
	[key: string]: unknown;
}

const toNumber = (...values: Array<string | number | null | undefined>) => {
	for (const value of values) {
		if (value === null || value === undefined || value === "") continue;
		const parsed = Number(value);
		if (Number.isFinite(parsed)) return parsed;
	}
	return 0;
};

export function mapApiResponseToOrder(order: OrderApiResponse): Order {
	const metadata = (order.metadata ?? {}) as Record<string, unknown>;
	const isManual = metadata.channel === "manual";
	const metaText = (key: string) =>
		typeof metadata[key] === "string" && metadata[key]
			? (metadata[key] as string)
			: undefined;

	const baseCustomer =
		typeof order.customer === "string"
			? { name: order.customer }
			: (order.customer ?? { name: "" });
	// Manual (in-person) sales have no account: use what the merchant typed.
	const customer = {
		...baseCustomer,
		name:
			baseCustomer.name ||
			metaText("customerName") ||
			(isManual ? "Venda balcão" : ""),
		phone: baseCustomer.phone ?? metaText("customerPhone"),
	};

	const shippingAddress =
		order.shippingAddress ??
		(order.location ? { fullAddress: order.location as string } : undefined);

	return {
		id: order.id,
		orderNumber: order.orderNumber ?? order.id,
		status: (order.status as Order["status"]) ?? "open",
		paymentStatus: (order.paymentStatus as Order["paymentStatus"]) ?? "pending",
		fulfilmentStatus:
			(order.fulfilmentStatus as Order["fulfilmentStatus"]) ?? "unfulfilled",
		customer,
		shippingAddress,
		items: order.items ?? [],
		itemsCount: order.itemsCount ?? order.items?.length ?? 0,
		subtotal: toNumber(
			order.subtotal,
			order.subtotalBrl,
			order.total,
			order.totalAmountBrl,
		),
		shippingCost: toNumber(order.shippingCost, order.shippingAmountBrl),
		discount: toNumber(order.discount, order.discountAmountBrl),
		total: toNumber(order.total, order.totalAmountBrl),
		currency: order.currency ?? "BRL",
		paymentMethod: order.paymentMethod ?? undefined,
		userId: order.userId ?? undefined,
		metadata: order.metadata ?? undefined,
		shippingAddressId: order.shippingAddressId ?? undefined,
		paidAt: order.paidAt ?? undefined,
		shippedAt: order.shippedAt ?? undefined,
		deliveredAt: order.deliveredAt ?? undefined,
		canceledAt: order.canceledAt ?? undefined,
		notes: order.notes,
		thumbnail: order.thumbnail,
		createdAt: order.createdAt ?? order.date ?? new Date().toISOString(),
		updatedAt: order.updatedAt,
		storeId: order.storeId,
	};
}

export const getOrdersAdapter = async (storeId?: string): Promise<Order[]> => {
	const params = buildStoreParams(storeId);

	const response = await apiClient.get<
		| OrderApiResponse[]
		| {
				orders?: OrderApiResponse[];
				data?: OrderApiResponse[];
				items?: OrderApiResponse[];
		  }
	>("/orders/all", {
		params,
	});

	const data = response.data;
	const ordersList = Array.isArray(data)
		? data
		: data.orders || data.data || data.items || [];

	return ordersList.map(mapApiResponseToOrder);
};
